import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, GripVertical, Video, CheckSquare, TrendingUp, ListChecks, ChevronUp, ChevronDown } from 'lucide-react';
import { ProgramComponent, ProgramData } from '@/pages/add-program';
import ComponentModal from './component-modal';

interface Step3BuildComponentsProps {
  data: ProgramData;
  updateData: (updates: Partial<ProgramData>) => void;
  onValidationChange: (isValid: boolean) => void;
}

export default function Step3BuildComponents({ data, updateData, onValidationChange }: Step3BuildComponentsProps) {
  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<ProgramComponent | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const components = data.components || [];

  const handleAddComponent = () => {
    setEditingComponent(null);
    setEditingIndex(null);
    setComponentModalOpen(true);
  };

  const handleEditComponent = (component: ProgramComponent, index: number) => {
    setEditingComponent(component);
    setEditingIndex(index);
    setComponentModalOpen(true);
  };

  const handleDeleteComponent = (index: number) => {
    const updatedComponents = components.filter((_, i) => i !== index);
    // Reorder componentOrder
    const reorderedComponents = updatedComponents.map((comp, i) => ({
      ...comp,
      componentOrder: i + 1
    }));
    updateData({ components: reorderedComponents });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return; // Already at top
    
    const updatedComponents = [...components];
    [updatedComponents[index - 1], updatedComponents[index]] = 
      [updatedComponents[index], updatedComponents[index - 1]];
    
    // Update componentOrder
    const reorderedComponents = updatedComponents.map((comp, i) => ({
      ...comp,
      componentOrder: i + 1
    }));
    
    updateData({ components: reorderedComponents });
  };

  const handleMoveDown = (index: number) => {
    if (index === components.length - 1) return; // Already at bottom
    
    const updatedComponents = [...components];
    [updatedComponents[index], updatedComponents[index + 1]] = 
      [updatedComponents[index + 1], updatedComponents[index]];
    
    // Update componentOrder
    const reorderedComponents = updatedComponents.map((comp, i) => ({
      ...comp,
      componentOrder: i + 1
    }));
    
    updateData({ components: reorderedComponents });
  };

  const handleSaveComponent = (component: ProgramComponent) => {
    let updatedComponents: ProgramComponent[];
    
    if (editingIndex !== null) {
      // Editing existing component
      updatedComponents = components.map((comp, i) => 
        i === editingIndex ? { ...component, componentOrder: comp.componentOrder } : comp
      );
    } else {
      // Adding new component
      const newComponentOrder = components.length + 1;
      updatedComponents = [...components, { ...component, componentOrder: newComponentOrder }];
    }
    
    updateData({ components: updatedComponents });
    setComponentModalOpen(false);
  };

  const getComponentIcon = (type: string) => {
    switch (type) {
      case 'videoSeries': return <Video className="h-5 w-5" />;
      case 'repeatingTask': return <CheckSquare className="h-5 w-5" />;
      case 'steps': return <TrendingUp className="h-5 w-5" />;
      case 'dynamicTask': return <ListChecks className="h-5 w-5" />;
      default: return null;
    }
  };

  const getComponentSummary = (component: ProgramComponent) => {
    switch (component.componentType) {
      case 'videoSeries':
        return `${component.tasks.length} video${component.tasks.length !== 1 ? 's' : ''}`;
      case 'repeatingTask':
        return `${component.tasks.length} task${component.tasks.length !== 1 ? 's' : ''}`;
      case 'steps':
        return `Target: ${component.target.toLocaleString()} steps`;
      case 'dynamicTask':
        return `${component.tasks.length} task${component.tasks.length !== 1 ? 's' : ''}`;
      default:
        return '';
    }
  };

  const getComponentTypeName = (type: string) => {
    switch (type) {
      case 'videoSeries': return 'Video Series';
      case 'repeatingTask': return 'Repeating Task';
      case 'steps': return 'Steps';
      case 'dynamicTask': return 'Dynamic Task';
      default: return type;
    }
  };

  // Validate: at least one component required
  const isValid = components.length > 0;
  
  // Notify parent of validation status
  if (onValidationChange) {
    setTimeout(() => onValidationChange(isValid), 0);
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Program Components</h3>
          <p className="text-sm text-muted-foreground">Add and configure the components that make up this program</p>
        </div>
        <Button onClick={handleAddComponent} data-testid="button-add-component">
          <Plus className="h-4 w-4 mr-2" />
          Add Component
        </Button>
      </div>

      {components.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">No components added yet</p>
              <Button onClick={handleAddComponent} data-testid="button-add-first-component">
                <Plus className="h-4 w-4 mr-2" />
                Add First Component
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {components.map((component, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-1">
                    <GripVertical className="h-5 w-5 text-muted-foreground cursor-move" />
                    <div className="flex items-center gap-2">
                      {getComponentIcon(component.componentType)}
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{getComponentTypeName(component.componentType)}</Badge>
                          <span className="text-sm font-medium">#{component.componentOrder}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {getComponentSummary(component)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      data-testid={`button-move-up-${index}`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === components.length - 1}
                      data-testid={`button-move-down-${index}`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditComponent(component, index)}
                      data-testid={`button-edit-component-${index}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteComponent(index)}
                      data-testid={`button-delete-component-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Component Modal */}
      <Dialog open={componentModalOpen} onOpenChange={setComponentModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingComponent ? 'Edit Component' : 'Add Component'}</DialogTitle>
            <DialogDescription>
              Configure the component details below
            </DialogDescription>
          </DialogHeader>
          <ComponentModal
            component={editingComponent}
            onSave={handleSaveComponent}
            onCancel={() => setComponentModalOpen(false)}
            contentLibrary={data.contentLibrary}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
